// Smart code merging utility
// Intelligently merges new code with existing code instead of overwriting

import * as acorn from 'acorn'
import * as acornLoose from 'acorn-loose'

export function smartMergeCode(existingCode, newCode, fileType) {
  // If existing code is empty, just use new code
  if (!existingCode || existingCode.trim() === '') {
    return newCode
  }

  // If new code is empty, keep existing
  if (!newCode || newCode.trim() === '') {
    return existingCode
  }

  // Different strategies based on file type
  switch (fileType) {
    case 'js':
      return mergeJavaScript(existingCode, newCode)
    case 'css':
      return mergeCSS(existingCode, newCode)
    case 'html':
      return mergeHTML(existingCode, newCode)
    default:
      return newCode
  }
}

function mergeJavaScript(existing, newCode) {
  // Handle AI-style diff markers (// ... existing code ...)
  if (containsAIMarkers(newCode)) {
    return applyAIPatches(existing, newCode)
  }
  
  // Check if new code is a complete sketch (has setup and draw)
  const hasSetup = /function\s+setup\s*\(/.test(newCode)
  const hasDraw = /function\s+draw\s*\(/.test(newCode)
  
  // If it's a complete sketch replacement, use it
  if (hasSetup && hasDraw) {
    return newCode
  }

  // Extract function names from new code
  const newFunctions = extractFunctions(newCode)
  const newClasses = extractClasses(newCode)
  
  // If no functions or classes found, it might be partial code - append it
  if (newFunctions.length === 0 && newClasses.length === 0) {
    return existing + '\n\n// AI Addition:\n' + newCode
  }

  // Replace or add functions
  let merged = existing
  
  // Replace existing functions with new versions
  newFunctions.forEach(func => {
    // Find if this function exists in merged code using brace counting
    const existingFunc = findFunctionInCode(merged, func.name)
    
    if (existingFunc) {
      // Replace existing function with proper bounds
      merged = merged.slice(0, existingFunc.start) + func.code + merged.slice(existingFunc.end)
    } else {
      // Add new function at the end
      merged += '\n\n' + func.code
    }
  })

  // Replace or add classes
  newClasses.forEach(cls => {
    // Find if this class exists in merged code using brace counting
    const existingClass = findClassInCode(merged, cls.name)
    
    if (existingClass) {
      // Replace existing class with proper bounds
      merged = merged.slice(0, existingClass.start) + cls.code + merged.slice(existingClass.end)
    } else {
      // Add new class at the end
      merged += '\n\n' + cls.code
    }
  })

  return merged
}

// Find a specific function by name and return its bounds
function findFunctionInCode(code, funcName) {
  const funcStartRegex = new RegExp(`function\\s+${funcName}\\s*\\([^)]*\\)\\s*\\{`, 'g')
  const match = funcStartRegex.exec(code)
  
  if (!match) return null
  
  const startIndex = match.index
  const bodyStart = match.index + match[0].length - 1
  const endIndex = findMatchingBrace(code, bodyStart)
  
  if (endIndex === -1) return null
  
  return { start: startIndex, end: endIndex + 1 }
}

// Find a specific class by name and return its bounds
function findClassInCode(code, className) {
  const classStartRegex = new RegExp(`class\\s+${className}(?:\\s+extends\\s+\\w+)?\\s*\\{`, 'g')
  const match = classStartRegex.exec(code)
  
  if (!match) return null
  
  const startIndex = match.index
  const bodyStart = match.index + match[0].length - 1
  const endIndex = findMatchingBrace(code, bodyStart)
  
  if (endIndex === -1) return null
  
  return { start: startIndex, end: endIndex + 1 }
}

function mergeCSS(existing, newCode) {
  // For CSS, merge selectors (using Maps to preserve order)
  const existingRules = extractCSSRules(existing)
  const newRules = extractCSSRules(newCode)
  
  // Merge: new rules override existing ones with same selector, preserving order
  const merged = new Map(existingRules)
  for (const [selector, properties] of newRules) {
    merged.set(selector, properties)
  }
  
  return Array.from(merged.entries())
    .map(([selector, rules]) => `${selector} {\n${rules}\n}`)
    .join('\n\n')
}

function mergeHTML(existing, newCode) {
  // For HTML, if new code has full structure, use it
  if (newCode.includes('<!DOCTYPE') || newCode.includes('<html')) {
    return newCode
  }
  
  // Otherwise, it might be a snippet - keep existing
  return existing
}

// Helper functions

function extractFunctions(code) {
  const functions = []
  // Find function declarations and extract with brace counting
  const funcStartRegex = /function\s+(\w+)\s*\([^)]*\)\s*\{/g
  let match
  
  while ((match = funcStartRegex.exec(code)) !== null) {
    const name = match[1]
    const startIndex = match.index
    const bodyStart = match.index + match[0].length - 1 // Position of opening {
    
    // Count braces to find the matching closing brace
    const endIndex = findMatchingBrace(code, bodyStart)
    if (endIndex !== -1) {
      functions.push({
        name: name,
        code: code.slice(startIndex, endIndex + 1)
      })
    }
  }
  
  return functions
}

// Helper to find matching closing brace using brace counting
function findMatchingBrace(code, openBraceIndex) {
  let depth = 1
  let i = openBraceIndex + 1
  let inString = false
  let stringChar = ''
  let inComment = false
  let inLineComment = false
  
  while (i < code.length && depth > 0) {
    const char = code[i]
    const prev = code[i - 1]
    const next = code[i + 1]
    
    // Handle line comments
    if (!inString && !inComment && char === '/' && next === '/') {
      inLineComment = true
      i += 2
      continue
    }
    if (inLineComment && char === '\n') {
      inLineComment = false
      i++
      continue
    }
    if (inLineComment) {
      i++
      continue
    }
    
    // Handle block comments
    if (!inString && !inComment && char === '/' && next === '*') {
      inComment = true
      i += 2
      continue
    }
    if (inComment && char === '*' && next === '/') {
      inComment = false
      i += 2
      continue
    }
    if (inComment) {
      i++
      continue
    }
    
    // Handle strings
    if (!inString && (char === '"' || char === "'" || char === '`')) {
      inString = true
      stringChar = char
      i++
      continue
    }
    if (inString && char === stringChar && prev !== '\\') {
      inString = false
      i++
      continue
    }
    if (inString) {
      i++
      continue
    }
    
    // Count braces
    if (char === '{') depth++
    if (char === '}') depth--
    
    i++
  }
  
  return depth === 0 ? i - 1 : -1
}

function extractClasses(code) {
  const classes = []
  // Find class declarations and extract with brace counting
  const classStartRegex = /class\s+(\w+)(?:\s+extends\s+\w+)?\s*\{/g
  let match
  
  while ((match = classStartRegex.exec(code)) !== null) {
    const name = match[1]
    const startIndex = match.index
    const bodyStart = match.index + match[0].length - 1 // Position of opening {
    
    // Count braces to find the matching closing brace
    const endIndex = findMatchingBrace(code, bodyStart)
    if (endIndex !== -1) {
      classes.push({
        name: name,
        code: code.slice(startIndex, endIndex + 1)
      })
    }
  }
  
  return classes
}

function extractCSSRules(css) {
  // Use Map to preserve selector order (plain objects don't guarantee order)
  const rules = new Map()
  const ruleRegex = /([^{]+)\{([^}]+)\}/g
  let match
  
  while ((match = ruleRegex.exec(css)) !== null) {
    const selector = match[1].trim()
    const properties = match[2].trim()
    rules.set(selector, properties)
  }
  
  return rules
}

// AI-aware patching functions (handle "// ... existing code ..." markers)
function containsAIMarkers(code) {
  // Check for common AI placeholder patterns
  return /\/\/\s*\.\.\..*?(existing|unchanged|rest of|other).*code/i.test(code) ||
         /\/\*\s*\.\.\..*?(existing|unchanged|rest of|other).*code.*\*\//i.test(code)
}

/**
 * Validate JavaScript code syntax
 * @param {string} code - The code to validate
 * @returns {{ valid: boolean, error?: { line: number, column: number, message: string } }}
 */
export function validateJS(code) {
  try {
    acorn.parse(code, { 
      ecmaVersion: 2020, 
      sourceType: 'script',
      allowReserved: true
    })
    return { valid: true }
  } catch (e) {
    return { 
      valid: false, 
      error: {
        line: e.loc?.line || 1,
        column: e.loc?.column || 0,
        message: e.message?.replace(/\(\d+:\d+\)$/, '').trim() || 'Syntax error'
      }
    }
  }
}

/**
 * Parse JavaScript code with error tolerance (won't throw on invalid code)
 * Useful for extracting structure from partially broken code
 * @param {string} code - The code to parse
 * @returns {object} - AST (may be incomplete if code has errors)
 */
export function parseLoose(code) {
  return acornLoose.parse(code, {
    ecmaVersion: 2020,
    sourceType: 'script'
  })
}

/**
 * Get all function and class names from code (error-tolerant)
 * @param {string} code - The code to analyze
 * @returns {{ functions: string[], classes: string[] }}
 */
export function getCodeStructure(code) {
  try {
    const ast = acornLoose.parse(code, {
      ecmaVersion: 2020,
      sourceType: 'script'
    })
    
    const functions = []
    const classes = []
    
    // Walk the AST to find function and class declarations
    for (const node of ast.body) {
      if (node.type === 'FunctionDeclaration' && node.id?.name) {
        functions.push(node.id.name)
      }
      if (node.type === 'ClassDeclaration' && node.id?.name) {
        classes.push(node.id.name)
      }
    }
    
    return { functions, classes }
  } catch (e) {
    // Fallback to regex if AST parsing fails completely
    return {
      functions: extractFunctions(code).map(f => f.name),
      classes: extractClasses(code).map(c => c.name)
    }
  }
}

function applyAIPatches(existing, patchCode) {
  // Split existing code into lines for context matching
  const existingLines = existing.split('\n')
  let result = []
  
  // Split patch into sections (separated by AI markers)
  const sections = patchCode.split(/\/\/\s*\.\.\..+code.*\n?/i)
  
  // If we have clear sections with context, try to match them
  if (sections.length > 1) {
    // Try to find where each section belongs in the existing code
    let existingIndex = 0
    
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i].trim()
      if (!section) continue
      
      // Try to find this section in the existing code
      const sectionLines = section.split('\n')
      const firstLine = sectionLines[0].trim()
      
      // Find matching context in existing code
      let matchIndex = -1
      for (let j = existingIndex; j < existingLines.length; j++) {
        if (existingLines[j].trim().includes(firstLine)) {
          matchIndex = j
          break
        }
      }
      
      if (matchIndex !== -1) {
        // Add skipped existing code
        for (let j = existingIndex; j < matchIndex; j++) {
          result.push(existingLines[j])
        }
        // Add the new section
        result.push(...sectionLines)
        existingIndex = matchIndex + sectionLines.length
      } else {
        // No match found, append section
        result.push(...sectionLines)
      }
    }
    
    // Add remaining existing code
    for (let j = existingIndex; j < existingLines.length; j++) {
      result.push(existingLines[j])
    }
    
    return result.join('\n')
  }
  
  // Fallback: remove AI markers and use as-is
  return patchCode.replace(/\/\/\s*\.\.\..+code.*/gi, '')
}
